import md5 from "crypto-js/md5";
import sanitizer from "sanitize-html";
import { isEqual, cloneDeep, unset } from "lodash";
import { S3Client } from "@aws-sdk/client-s3";
import { Upload } from "@aws-sdk/lib-storage";
import { XhrHttpHandler } from "@aws-sdk/xhr-http-handler";
import { CognitoIdentityClient } from "@aws-sdk/client-cognito-identity";
import { fromCognitoIdentityPool } from "@aws-sdk/credential-provider-cognito-identity";
import {
  isPanelistCountValid,
  getTextLengthAsQuillJS,
  isValidURL,
  calculatePathErrors,
  getFigmaNodeIdFromUrl,
  getTestTypeForRecruit,
} from "./common";
import {
  YESNO,
  TEST_TYPE,
  TASK_TYPES,
  MAX_LENGTH,
  TEST_STATUS,
  LIKERT_TYPES,
  SURVEY_TYPES,
  SUCCESS_CRITERIA,
  RECRUIT_AGE_LIMITS,
  RECRUIT_CREDITS_ERR,
  RECRUIT_DEMOGRAPHICS_ERR,
  URL_PATTERN,
  TOOLKIT_TEST_STATUS,
  RECRUIT_NO_PARTICIPANT_ERR,
  RECRUIT_SELF_SOURCED_INSUFFICEINT_ERR,
  ENTERPRISE_RECRUIT_SELF_SOURCED_INSUFFICEINT_ERR,
  ENTERPRISE_RECRUIT_CREDITS_ERR,
  PAID_RECRUIT_CREDITS_ERR,
  EXTRA_UA_CREDITS_ERR,
} from "../actions/constants";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";

dayjs.extend(utc);
dayjs.extend(timezone);

const { TEXT, LIKERT_5PT, LIKERT_7PT, LIKERT_10PT, CHECKBOX } = SURVEY_TYPES;
const { ANSWER, SUCCESS_URL, TIME_COMPLETION } = SUCCESS_CRITERIA;
const { EMPTY, DEFAULT, IMAGE, SURVEY, MOBAPP_PROTOTYPE, EXTERNAL_PROTOTYPE, GROUP, FIRSTCLICK, FIVESEC } = TASK_TYPES;
const { DRAFT, ACTIVE, COMPLETED, EDIT_TEST, INACTIVE, SAVED_TEST, UI_STATUS_EXPIRED, UI_STATUS_COMPLETED, UI_STATUS_TERMINATED } =
  TEST_STATUS;

export const sanitizedText = (textWithHTML) => sanitizer(textWithHTML, { allowedTags: [] });

export const hasDuplicateValues = (arr) => {
  return arr.reduce(({ arr, hasDuplicates }, opt) => ({ hasDuplicates: hasDuplicates || arr.includes(opt), arr: [...arr, opt] }), {
    arr: [],
    hasDuplicates: false,
  })?.hasDuplicates;
};

export const isValidTest = (
  data,
  data_launched,
  user,
  isFreePlan,
  isCustomPlan,
  hasResponseSubscription,
  responsesCollected,
  allowedFeatures,
  cancel_at,
  organization = {}
) => {
  const { test, tasks, settings } = data;
  const { intro_text, complete_text } = settings;
  const { settings: launchedSettings } = data_launched;

  if (
    isTestError(test) ||
    isTasksError(tasks, test.test_type) ||
    isRecruitError(
      test,
      settings,
      launchedSettings,
      user,
      isFreePlan,
      isCustomPlan,
      hasResponseSubscription,
      responsesCollected,
      allowedFeatures,
      cancel_at,
      organization
    ) ||
    isGreetingsError(intro_text, complete_text)
  )
    return false;

  return true;
};

// Test Validation
export function isTestError(test) {
  const { name, internal_name } = test;
  if (!name.trim() || !internal_name.trim()) return true;

  return false;
}

// Tasks Validation
export function isTasksError(tasks, test_type) {
  return tasks.some((task) => validateTask(task, test_type)?.error);
}
export function isGreetingsError(intro_text, complete_text) {
  return validateGreeting(true, intro_text)?.error || validateGreeting(false, complete_text)?.error;
}

export function validateGreeting(isWelcomeGreeting, greetingText) {
  if (!greetingText.trim() || getTextLengthAsQuillJS(greetingText) === 0)
    return { error: true, errorMsg: `Please provide a ${isWelcomeGreeting ? "welcome" : "thank you"} message` };
  return { error: false, errorMsg: "" };
}

export function validateTask(task, test_type) {
  const {
    url,
    type,
    answer,
    images,
    options,
    subTasks,
    addImages,
    start_urls,
    surveyType,
    description,
    // answer_type,
    success_type,
    five_second,
    expected_time,
    successUrlsArr,
    task_image_url,
    mobile_app_name,
    likertOptionType,
  } = task;

  let resultObj = { error: false, errorMsg: "" };
  const isMultiOSApp = Number(test_type) === TEST_TYPE.MULTIOS_APP;
  const maxDescLength = type === SURVEY ? MAX_LENGTH?.SURVEY_QUESTION : MAX_LENGTH.TASK_DESCRIPTION;

  // Group Task
  if (type === GROUP) {
    if (isTasksError(subTasks, test_type)) resultObj = { error: true, errorMsg: "One of the subtask has an error" };
    return resultObj;
  }

  // Empty Task - No Task Type
  if (type === EMPTY) {
    resultObj = { error: true, errorMsg: "Please include task type from the task type panel." };
  } else if (getTextLengthAsQuillJS(description) === 0) {
    // Common Error
    resultObj = { error: true, errorMsg: "Please fill the description." };
  } else if (getTextLengthAsQuillJS(description) > maxDescLength) {
    resultObj = { error: true, errorMsg: `Task Description is too long (maximum is ${maxDescLength} characters).` };
  } else if (type === DEFAULT && success_type === TIME_COMPLETION && Number(expected_time) < 5) {
    resultObj = { error: true, errorMsg: "Please fill the expected time." };
  } else if (type === DEFAULT && !isMultiOSApp) {
    // Default Task
    if (url?.trim() === "" || !isValidURL(url)) {
      resultObj = { error: true, errorMsg: `Please fill the ${url?.trim() === "" ? "website" : "valid"} URL` };
    } else if (success_type === ANSWER && (!answer || answer?.toString().trim() === "")) {
      resultObj = { error: true, errorMsg: "Please fill the answer." };
    } else if (
      success_type === SUCCESS_URL &&
      successUrlsArr.some(({ url, urlpattern }) => url === "" || (Number(urlpattern) === URL_PATTERN.EXACT && !isValidURL(url)))
    ) {
      resultObj = { error: true, errorMsg: "Please fill the success urls." };
      // } else if (success_type === SUCCESS_URL && successUrlsArr.some((s) => !isSameURL(url, s.url))) {
      //   resultObj = { error: true, errorMsg: "Success url should be the same as the website url." };
    }
  } else if ([IMAGE, FIRSTCLICK, FIVESEC].includes(type) && !task_image_url) {
    // Image Task
    resultObj = { error: true, errorMsg: "Please upload a image." };
  } else if (type === MOBAPP_PROTOTYPE && (url?.trim() === "" || mobile_app_name?.trim() === "")) {
    // Mobile App
    resultObj = { error: true, errorMsg: `Please fill the ${url?.trim() === "" ? "Link to Your Mobile App." : "App Name."}` };
  } else if (type === SURVEY) {
    // Survey Task
    if (addImages && images.length === 0) resultObj = { error: true, errorMsg: "Please upload the images." };

    if (
      surveyType !== TEXT ||
      ([LIKERT_5PT, LIKERT_7PT, LIKERT_10PT].includes(surveyType) && likertOptionType === LIKERT_TYPES.CUSTOM)
    ) {
      if (hasDuplicateValues(options?.map(({ name }) => name))) {
        resultObj = { error: true, errorMsg: "Please remove duplicate options." };
      }
      if (options.some(({ name }) => name?.trim() === "")) resultObj = { error: true, errorMsg: "Please fill all the options." };
    }
    if (surveyType === CHECKBOX && options.length < 2) resultObj = { error: true, errorMsg: "Please add at least two options." };
  } else if (type === EXTERNAL_PROTOTYPE) {
    // Prototype Task
    if (!url?.trim()) resultObj = { error: true, errorMsg: "Please add or select a prototype." };
    else if ((success_type === TIME_COMPLETION || Number(five_second) === 1) && Number(expected_time) < 5) {
      resultObj = { error: true, errorMsg: "Please fill the expected time." };
    } else if (success_type === SUCCESS_URL && successUrlsArr?.length === 0) {
      resultObj = { error: true, errorMsg: "Please add at least one success path." };
    } else if (success_type === SUCCESS_URL && successUrlsArr.some(({ url }) => !url || url?.trim() === "")) {
      resultObj = { error: true, errorMsg: "Please select end screen for all success paths." };
    } else if (
      success_type === SUCCESS_URL &&
      successUrlsArr.some(
        ({ url, urlpattern }, i) =>
          Number(urlpattern) === URL_PATTERN.PATH_URLS &&
          calculatePathErrors(successUrlsArr, {
            currentPathIndex: i,
            currentPathIds: JSON.parse(url.replace(/http:\/\/www\./, "https://"))
              ?.map(getFigmaNodeIdFromUrl)
              ?.filter(Boolean),
          })?.hasError
      )
    ) {
      resultObj = { error: true, errorMsg: "Please resolve the path errors before proceeding." };
    } else if (success_type === ANSWER && (!answer || answer.toString().trim() === "")) {
      resultObj = { error: true, errorMsg: "Please fill the answer." };
    } else if (success_type === TIME_COMPLETION && Number(expected_time) < 5) {
      resultObj = { error: true, errorMsg: "Please fill the expected time." };
    }
  } else if (isMultiOSApp && type === DEFAULT) {
    if (mobile_app_name?.trim() === "") {
      resultObj = { error: true, errorMsg: "Please fill the App Name." };
    } else if (start_urls?.ios?.trim() === "" || !isValidURL(start_urls?.ios)) {
      resultObj = { error: true, errorMsg: "Please fill the Link to Your iOS Mobile App." };
    } else if (start_urls?.android?.trim() === "" || !isValidURL(start_urls?.android)) {
      resultObj = { error: true, errorMsg: "Please fill the Link to Your Android Mobile App." };
    }
  }

  return resultObj;
}

// Settings Validation
export function isRecruitError(
  test,
  settings,
  launchedSettings,
  user,
  isFreePlan,
  isCustomPlan,
  hasResponseSubscription,
  responsesCollected,
  allowedFeatures,
  cancel_at,
  organization = {}
) {
  const { users_max, visitors_max } = settings || {};
  const { credits_panel, credits_response_total } = organization || {};
  const testType = getTestTypeForRecruit(test);
  const credits = user?.credits?.[testType];

  const {
    ua_panel: { enabled: isUAPanelEnabled = true },
    ua_panel_topup: { enabled: isUAPanelTopupEnabled = true },
  } = allowedFeatures || {};

  const LOWER_AGE = {
    MIN: RECRUIT_AGE_LIMITS.MIN,
    MAX: launchedSettings?.age_min ? Number(launchedSettings.age_min) : RECRUIT_AGE_LIMITS.MAX,
  };
  const UPPER_AGE = {
    MIN: launchedSettings?.age_max ? Number(launchedSettings.age_max) : RECRUIT_AGE_LIMITS.MIN,
    MAX: RECRUIT_AGE_LIMITS.MAX,
  };
  const minAge = launchedSettings?.age_min ? Number(launchedSettings.age_min) : Number(settings.age_min);
  const maxAge = launchedSettings?.age_max ? Number(launchedSettings.age_max) : Number(settings.age_max);

  if (users_max === 0 && visitors_max === 0) {
    return RECRUIT_NO_PARTICIPANT_ERR;
  }

  const estimatedPlatformCredits = Number(visitors_max + users_max) * Number(credits?.random);
  const insufficientPlatformCredits = Math.abs(estimatedPlatformCredits) - Number(credits_response_total) > 0;
  if (isFreePlan && !hasResponseSubscription && insufficientPlatformCredits) {
    return RECRUIT_SELF_SOURCED_INSUFFICEINT_ERR;
  }
  if ((isCustomPlan || !hasResponseSubscription || (hasResponseSubscription && cancel_at)) && insufficientPlatformCredits) {
    return ENTERPRISE_RECRUIT_SELF_SOURCED_INSUFFICEINT_ERR;
  }

  const extraUAs = Number(users_max) - Number(responsesCollected?.panelTesters || 0);
  if (!isUAPanelEnabled && extraUAs > 0) {
    return EXTRA_UA_CREDITS_ERR;
  }

  const insufficientUACredits = !isPanelistCountValid(responsesCollected, users_max, credits?.uxarmy, credits_panel);
  // free
  if (!isUAPanelEnabled && insufficientUACredits) {
    return RECRUIT_CREDITS_ERR;
  }
  // enterprise
  if (isUAPanelEnabled && !isUAPanelTopupEnabled && insufficientUACredits) {
    return ENTERPRISE_RECRUIT_CREDITS_ERR;
  }
  // paid
  if (isUAPanelEnabled && isUAPanelTopupEnabled && insufficientUACredits) {
    return PAID_RECRUIT_CREDITS_ERR;
  }

  if (!(minAge < maxAge && minAge >= LOWER_AGE.MIN && minAge <= LOWER_AGE.MAX && maxAge >= UPPER_AGE.MIN && maxAge <= UPPER_AGE.MAX)) {
    return RECRUIT_DEMOGRAPHICS_ERR;
  }

  return false;
}

const keysToIgnore = ["published", "shuffle_status", "sus_status", "email_lang", "intro_text", "complete_text"];
const removeKeys = (obj, keys) => {
  keys.forEach((key) => {
    unset(obj, key);
  });
  if (obj && obj.test) {
    unset(obj.test, "isLogicOpenedFirstTime");
  }
  if (obj && obj.tasks && obj.tasks.length) {
    obj.tasks.forEach((task) => {
      unset(task, "chosen");
      unset(task, "selected");
      unset(task, "isNew");
    });
  }
  return obj;
};
export const isStateUnchangedWithIgnoredKeys = (currentState, publishedState) => {
  const filteredCurrentState = removeKeys(cloneDeep(currentState), keysToIgnore);
  const filteredPublishedState = removeKeys(cloneDeep(publishedState), keysToIgnore);
  return isEqual(filteredCurrentState, filteredPublishedState);
};

// no use now as backend is sending the information
export const getTestStatus = (testStatus) => {
  const status = {
    [DRAFT]: {
      label: "Draft",
      icon: "testDraft",
      className: "color-365198 border-365198 bg-white-6",
      classNameNew: "bg-EEF2F6 color-364152 br-8px fw-400 border-0 p-4-8",
    },
    [EDIT_TEST]: {
      label: "Draft",
      icon: "testDraft",
      className: "color-365198 border-365198 bg-white-6",
      classNameNew: "bg-EEF2F6 color-364152 br-8px fw-400 border-0 p-4-8",
    },
    [SAVED_TEST]: {
      label: "Draft",
      icon: "testDraft",
      className: "color-365198 border-365198 bg-white-6",
      classNameNew: "bg-EEF2F6 color-364152 br-8px fw-400 border-0 p-4-8",
    },
    [ACTIVE]: {
      label: "Active",
      icon: "testActive",
      className: "color-0B3FE6 border-0b3fe6 bg-F5F8FF",
      classNameNew: "bg-F0FAFF text-2E62E9 br-8px fw-400 border-0 p-4-8",
    },
    [INACTIVE]: {
      label: "Pause",
      icon: "testPause",
      className: "color-365198 border-365198 bg-white-6",
      classNameNew: "bg-EEF2F6 color-364152 br-8px fw-400 border-0 p-4-8",
    },
    [COMPLETED]: {
      label: "Completed",
      icon: "testCompleted",
      className: "color-46AA58 border-46AA58 bg-46AA58_01",
      classNameNew: "bg-EEFBF4 color-17663A br-8px fw-400 border-0 p-4-8",
    }, // same for UI_STATUS_COMPLETED
    [UI_STATUS_EXPIRED]: {
      label: "Expired",
      icon: "testExpired",
      className: "color-365198 border-365198 bg-white-6",
      classNameNew: "bg-EEF2F6 color-364152 br-8px fw-400 border-0 p-4-8",
    },
    [UI_STATUS_TERMINATED]: {
      label: "Terminated",
      icon: "testTerminated",
      className: "color-365198 border-365198 bg-white-6",
      classNameNew: "bg-EEF2F6 color-364152 br-8px fw-400 border-0 p-4-8",
    },
  };

  return status[testStatus === UI_STATUS_COMPLETED ? COMPLETED : testStatus];
};

export const generateSlug = (randomNumber) => {
  const timestamp = Date.now();
  const uniquePart = `${randomNumber}-${timestamp}`;
  const hash = md5(uniquePart).toString();
  const slug = hash.substr(0, 8);
  return slug;
};

// Get dayjs date
export const getFormatedDate = (d = dayjs()) => d.format("YYYY-MM-DD HH:mm:ss");

export const moderatedTestAddExtraFields = (test, increaseResponses = false) => {
  const sessions = test?.sessions || [];
  let total_sessions = Number(test?.total_sessions) || 0;
  let total_completed_sessions = Number(test?.total_completed_sessions) || 0;

  // Increase the total sessions and completed sessions
  if (increaseResponses) {
    total_sessions += 1;
    total_completed_sessions += 1;
  }

  return {
    ...test,
    sessions,
    total_sessions,
    total_completed_sessions,
    hasSessions: sessions.length > 0,
    isCompleted: Number(test?.status) === COMPLETED,
    start_date: getFormatedDate(dayjs(test?.start_date)),
    isCollabTest: Number(test?.is_collab_test) === YESNO.YES,
    transcription_enabled: Number(test?.transcription_enabled),
    hasCollaborator: Number(test?.has_collaborator) === YESNO.YES,
    isInstantSession: Number(test?.test_type) === TEST_TYPE.INSTANT_SESSION,
    end_date: !test?.end_date || test?.end_date === "0000-00-00 00:00:00" ? "" : getFormatedDate(dayjs(test?.end_date)),
  };
};

// Upload File to S3
export const uploadFileToS3 = async (file, awsParams, onProgress = () => {}) => {
  const { filePath, region, bucketName, identityPoolId } = awsParams;

  // Initialize the Amazon Cognito credentials provider
  const client = new S3Client({
    region,
    requestHandler: new XhrHttpHandler({}),
    credentials: fromCognitoIdentityPool({ identityPoolId, client: new CognitoIdentityClient({ region }) }),
  });

  // Upload Params
  const params = {
    Body: file,
    Key: filePath,
    Bucket: bucketName,
    ACL: "public-read",
    ContentType: file.type,
    ContentDisposition: "attachment",
    queueSize: navigator?.hardwareConcurrency || 2,
  };

  try {
    const S3Upload = new Upload({ client, params });

    // Start tracking the UploadProgress
    S3Upload.on("httpUploadProgress", (e) => onProgress(Math.ceil((e?.loaded / e?.total) * 100)));

    return await S3Upload.done();
  } catch (e) {
    return e;
  }
};

// Timezone string
export const getTimezoneString = (timezone = dayjs.tz.guess()) => `GMT ${dayjs().tz(timezone).format("Z")} ${timezone}`;

// Get Toolkit Status As Yii Status's
export const getToolkitStatusAsYii = (toolkitStatus = TOOLKIT_TEST_STATUS?.DRAFT) => {
  if (toolkitStatus === TOOLKIT_TEST_STATUS?.DRAFT) return TEST_STATUS?.DRAFT;
  if (toolkitStatus === TOOLKIT_TEST_STATUS?.ACTIVE) return TEST_STATUS?.ACTIVE;
  if (toolkitStatus === TOOLKIT_TEST_STATUS?.EDIT) return TEST_STATUS?.EDIT_TEST;
  if (toolkitStatus === TOOLKIT_TEST_STATUS?.INACTIVE) return TEST_STATUS?.INACTIVE;
  if (toolkitStatus === TOOLKIT_TEST_STATUS?.COMPLETED) return TEST_STATUS?.COMPLETED;
  if (toolkitStatus === TOOLKIT_TEST_STATUS?.DELETED) return TEST_STATUS?.UI_STATUS_TERMINATED;

  return toolkitStatus;
};
